import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { Option } from "../../models/Option";
import { ResponseModel } from "../../models/ResponseModel";
import { VideoPayout, VideoPayoutPurchase } from "../../models/VideoPayout";
import { getPendingVideoPayoutPurchases, getPendingVideoPayouts, getVideoLessonTeachers, getVideoPayoutPurchases, getVideoPayouts, transferVideoPayout } from "../../services/VideoLessonService";
import { formatAmount, getFormatedDate, getLastMonthEndDate, getVideoPayoutSlipUrl, validatePaySlip } from "../../helpers/Common";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { useAlert } from "../../hooks/useAlert";
import { Header } from "../../components/shared/Header";
import { Button } from "../../components/shared/Button";
import { Select } from "../../components/shared/Select";
import { NoData } from "../../components/shared/NoData";
import { Pagination } from "../../components/shared/Pagination";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { TableLoader } from "../../components/loaders/TableLoader";
import { SelectLoader } from "../../components/loaders/SelectLoader";

const PAGE_SIZE = 40;

const TABS = [
    { tab: "PENDING", label: "Pending" },
    { tab: "HISTORY", label: "History" }
];

export const VideoPayoutsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("videoPayoutsPage"));
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();
    const tab: string = searchParams.get("tab") ?? "PENDING";
    // Cut-off for pending payouts; purchases on this date are included
    const periodEndDate: string = searchParams.get("date") ?? getLastMonthEndDate();
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const teacherId: number | undefined = searchParams.get("teacherId") === null ? undefined : +searchParams.get("teacherId")!;
    const [teachers, setTeachers] = useState<Option[]>();
    const [pendingPayouts, setPendingPayouts] = useState<VideoPayout[]>();
    const [payouts, setPayouts] = useState<VideoPayout[]>();
    const [transferringPayout, setTransferringPayout] = useState<VideoPayout | null>(null);
    const [viewingPayout, setViewingPayout] = useState<VideoPayout | null>(null);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();
    const { Alert, openAlert } = useAlert();

    useEffect(() => {
        if (admin === null) {
            // Callback url defaults to the current path, so the admin returns to this tab (with its filters) after login
            toAdminLogin("Please login to manage video lesson payouts.");
            return;
        }
        let active = true;
        (async () => {
            try {
                const teacherList: Option[] = await getVideoLessonTeachers(admin);
                if (active) setTeachers(teacherList);
            } catch (error) {
                if (active) toErrorPage(error);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (admin === null) return;
        // Ignore a slow response once the filters have changed again
        let active = true;
        loadPayouts(() => active);
        window.scrollTo(0, 0);
        return () => { active = false; };
    }, [tab, periodEndDate, page, teacherId]);

    useEffect(() => {
        setStyles(styleContext.getComponentStyle("videoPayoutsPage"));
    }, [isMobile]);

    async function loadPayouts(isActive: () => boolean = () => true) {
        try {
            if (tab === "PENDING") {
                setPendingPayouts(undefined);
                const payoutList: VideoPayout[] = await getPendingVideoPayouts(admin!, periodEndDate);
                if (isActive()) setPendingPayouts(payoutList);
            }
            else {
                setPayouts(undefined);
                const payoutList: VideoPayout[] = await getVideoPayouts(admin!, page, PAGE_SIZE, teacherId);
                if (isActive()) setPayouts(payoutList);
            }
        } catch (error) {
            if (isActive()) toErrorPage(error);
        }
    }

    // Filters live in the url so refresh and back keep the admin's place
    const updateSearchParam = (name: string, value?: string) => {
        if (value) {
            searchParams.set(name, value);
        }
        else {
            searchParams.delete(name);
        }
        if (name !== "page") {
            searchParams.delete("page");
        }
        navigate(`?${searchParams}`);
    }

    const onTransferDone = async (success: boolean, message: string) => {
        setTransferringPayout(null);
        openAlert(success ? "success" : "error", message);
        // Reload either way: a failed transfer usually means the pending purchases changed
        await loadPayouts();
    }

    if (admin === null) return null;

    const totalPages = payouts && payouts.length > 0 ? Math.ceil(payouts[0].recordCount / PAGE_SIZE) : 0;

    return (
        <>
            <Header />
            <div style={styles}>
                <h1 style={styles.heading}>{"Video Lesson Payouts"}</h1>
                <div style={styles.toolbar}>
                    {TABS.map((item) =>
                        <Button
                            key={item.tab}
                            style={{...styles.tabButton, ...(tab === item.tab && styles.tabButton.active)}}
                            onClick={() => updateSearchParam("tab", item.tab)}
                        >
                            {item.label}
                        </Button>
                    )}
                    {tab === "PENDING" ?
                    <label style={styles.dateWrapper}>
                        <span style={styles.dateLabel}>{"Purchases up to"}</span>
                        <input type="date" style={styles.dateInput} value={periodEndDate} onChange={(e) => updateSearchParam("date", e.target.value)} />
                    </label> :
                    teachers === undefined ? <SelectLoader count={1} /> :
                    <Select
                        styles={styles.teacherSelect}
                        label={"Teachers"}
                        options={teachers}
                        initialValue={teacherId?.toString()}
                        getSelectedValue={(value?: number) => updateSearchParam("teacherId", value?.toString())}
                    />}
                    <Button style={styles.backButton} onClick={() => navigate('/video-lessons')}>{"Video Lessons"}</Button>
                </div>
                {tab === "PENDING" ?
                <PendingPayouts
                    payouts={pendingPayouts}
                    styles={styles}
                    onTransfer={(payout) => setTransferringPayout(payout)}
                /> :
                <>
                    <PayoutHistory payouts={payouts} admin={admin} styles={styles} onSelect={(payout) => setViewingPayout(payout)} />
                    {totalPages > 1 && <Pagination page={page} totalPages={totalPages} handlePagination={(pageNo) => updateSearchParam("page", pageNo.toString())} />}
                </>}
            </div>
            {transferringPayout &&
            <TransferDialog
                admin={admin}
                payout={transferringPayout}
                styles={styles.payoutDialog}
                onClose={() => setTransferringPayout(null)}
                onDone={onTransferDone}
            />}
            {viewingPayout &&
            <PayoutDetailDialog
                admin={admin}
                payout={viewingPayout}
                styles={styles.payoutDialog}
                onClose={() => setViewingPayout(null)}
            />}
            <Alert />
        </>
    )
}

interface PendingPayoutsProps {
    payouts?: VideoPayout[];
    styles: any;
    onTransfer(payout: VideoPayout): void;
}

const PendingPayouts: React.FC<PendingPayoutsProps> = ({ payouts, styles, onTransfer }) => {
    if (payouts === undefined) return <TableLoader columnCount={5} rowCount={6} />;
    if (payouts.length === 0) return <NoData message={"No purchases are waiting to be transferred up to this date."} />;

    const totalNet = payouts.reduce((total, payout) => total + payout.netAmount, 0);
    return (
        <>
            <div style={styles.summary}>{`${payouts.length} teacher${payouts.length === 1 ? "" : "s"} to pay · ${formatAmount(totalNet)} in total`}</div>
            <table style={styles.table}>
                <thead>
                    <tr style={styles.table.header}>
                        <th style={styles.table.teacherColumn}>{"Teacher"}</th>
                        <th style={styles.table.purchasesColumn}>{"Purchases"}</th>
                        <th style={styles.table.amountColumn}>{"Amount"}</th>
                        <th style={styles.table.bankColumn}>{"Bank Account"}</th>
                        <th style={styles.table.actionColumn}>{"Action"}</th>
                    </tr>
                </thead>
                <tbody>
                    {payouts.map((payout) =>
                        <tr key={payout.teacherId} style={styles.table.tr}>
                            <td style={styles.table.leftCell}>
                                <div style={styles.table.primaryText}>{payout.teacherName}</div>
                                <div>{payout.whatsappNo}</div>
                            </td>
                            <td style={styles.table.leftCell}>
                                <div style={styles.table.primaryText}>{payout.purchaseCount}</div>
                                {payout.oldestPurchaseDate && <div style={styles.table.mutedText}>{`Oldest: ${getFormatedDate(payout.oldestPurchaseDate)}`}</div>}
                            </td>
                            <td style={styles.table.leftCell}>
                                <PayoutAmounts payout={payout} styles={styles.table} />
                            </td>
                            <td style={styles.table.leftCell}>
                                {payout.hasBankAccount ?
                                <BankDetails payout={payout} styles={styles.table} /> :
                                <div style={styles.table.warningText}>{"No bank details added"}</div>}
                            </td>
                            <td>
                                {/* Without bank details there is nowhere to send the money */}
                                <Button
                                    style={{...styles.table.actionButton, ...(!payout.hasBankAccount && styles.table.actionButton.disabled)}}
                                    disabled={!payout.hasBankAccount}
                                    onClick={() => onTransfer(payout)}
                                >
                                    {"Transfer"}
                                </Button>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </>
    );
}

interface PayoutHistoryProps {
    payouts?: VideoPayout[];
    admin: LoggedAdmin;
    styles: any;
    onSelect(payout: VideoPayout): void;
}

const PayoutHistory: React.FC<PayoutHistoryProps> = ({ payouts, admin, styles, onSelect }) => {
    const [hoveredPayoutId, setHoveredPayoutId] = useState<number>();

    if (payouts === undefined) return <TableLoader columnCount={6} rowCount={8} />;
    if (payouts.length === 0) return <NoData message={"No payouts have been transferred yet."} />;

    return (
        <table style={styles.table}>
            <thead>
                <tr style={styles.table.header}>
                    <th style={styles.table.idColumn}>{"Id"}</th>
                    <th style={styles.table.teacherColumn}>{"Teacher"}</th>
                    <th style={styles.table.purchasesColumn}>{"Purchases"}</th>
                    <th style={styles.table.amountColumn}>{"Amount"}</th>
                    <th style={styles.table.bankColumn}>{"Paid To"}</th>
                    <th style={styles.table.transferColumn}>{"Transfer"}</th>
                </tr>
            </thead>
            <tbody>
                {payouts.map((payout) =>
                    <tr
                        key={payout.videoLessonPayoutId}
                        style={{...styles.table.tr, ...styles.table.clickableTr, ...(payout.videoLessonPayoutId === hoveredPayoutId && styles.table.hoveredTr)}}
                        onMouseEnter={() => setHoveredPayoutId(payout.videoLessonPayoutId)}
                        onMouseLeave={() => setHoveredPayoutId(undefined)}
                        onClick={() => onSelect(payout)}
                    >
                        <td>{payout.videoLessonPayoutId}</td>
                        <td style={styles.table.leftCell}>
                            <div style={styles.table.primaryText}>{payout.teacherName}</div>
                            <div>{payout.whatsappNo}</div>
                        </td>
                        <td style={styles.table.leftCell}>
                            <div style={styles.table.primaryText}>{payout.purchaseCount}</div>
                            <div style={styles.table.mutedText}>{`Up to ${getFormatedDate(payout.periodEndDate)}`}</div>
                        </td>
                        <td style={styles.table.leftCell}>
                            <PayoutAmounts payout={payout} styles={styles.table} />
                        </td>
                        <td style={styles.table.leftCell}>
                            <BankDetails payout={payout} styles={styles.table} />
                        </td>
                        <td style={styles.table.leftCell}>
                            <div style={styles.table.primaryText}>{getFormatedDate(payout.createdDate!)}</div>
                            {payout.transferredByName && <div style={styles.table.mutedText}>{`By: ${payout.transferredByName}`}</div>}
                            {payout.referenceNumber && <div style={styles.table.mutedText}>{`Ref: ${payout.referenceNumber}`}</div>}
                            {/* Opening the slip shouldn't also open the detail dialog */}
                            {payout.hasSlip ?
                            <a style={styles.table.link} href={getVideoPayoutSlipUrl(payout.videoLessonPayoutId, admin.adminId, admin.token)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{"View slip"}</a> :
                            <div style={styles.table.mutedText}>{"No slip"}</div>}
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}

const PayoutAmounts: React.FC<{ payout: VideoPayout, styles: any }> = ({ payout, styles }) => (
    <>
        <div>{`Gross: ${formatAmount(payout.grossAmount)}`}</div>
        <div style={styles.mutedText}>{`Commission (${payout.commissionRate}%): ${formatAmount(payout.commissionAmount)}`}</div>
        <div style={styles.netText}>{`Net: ${formatAmount(payout.netAmount)}`}</div>
    </>
)

const BankDetails: React.FC<{ payout: VideoPayout, styles: any }> = ({ payout, styles }) => (
    <>
        <div style={styles.primaryText}>{payout.accountHolderName}</div>
        <div>{payout.accountNumber}</div>
        <div style={styles.mutedText}>{`${payout.bankName ?? ""}${payout.branchName ? " - " + payout.branchName : ""}`}</div>
    </>
)

interface SubjectGroup {
    videoSubjectId: number;
    subjects?: string;
    grades?: string;
    purchases: VideoPayoutPurchase[];
}

// The API returns purchases ordered by subject, so each subject's rows are already together
const groupBySubject = (purchases: VideoPayoutPurchase[]): SubjectGroup[] => {
    return purchases.reduce<SubjectGroup[]>((groups, purchase) => {
        const lastGroup = groups[groups.length - 1];
        if (lastGroup && lastGroup.videoSubjectId === purchase.videoSubjectId) {
            lastGroup.purchases.push(purchase);
        }
        else {
            groups.push({ videoSubjectId: purchase.videoSubjectId, subjects: purchase.subjects, grades: purchase.grades, purchases: [purchase] });
        }
        return groups;
    }, []);
}

interface TransferDialogProps {
    admin: LoggedAdmin;
    payout: VideoPayout;
    styles: any;
    onClose(): void;
    onDone(success: boolean, message: string): void;
}

const TransferDialog: React.FC<TransferDialogProps> = ({ admin, payout, styles, onClose, onDone }) => {
    const [purchases, setPurchases] = useState<VideoPayoutPurchase[]>();
    const [referenceNumber, setReferenceNumber] = useState<string>("");
    const [slipFile, setSlipFile] = useState<File>();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [toErrorPage] = useErrorPage();
    const { Alert, openAlert } = useAlert();

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const purchaseList: VideoPayoutPurchase[] = await getPendingVideoPayoutPurchases(admin, payout.teacherId, payout.periodEndDate);
                if (active) setPurchases(purchaseList);
            } catch (error) {
                if (active) toErrorPage(error);
            }
        })();
        return () => { active = false; };
    }, []);

    const handleSlipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Clear the input so choosing the same file again still fires a change
        event.target.value = "";
        if (!file) return;
        const validation = validatePaySlip(file);
        if (validation.valid) {
            setSlipFile(file);
        }
        else {
            openAlert("error", validation.invalidMessage!);
        }
    }

    const confirmTransfer = async () => {
        setIsSaving(true);
        try {
            const formData = new FormData();
            // The pending row goes back as is; the API recomputes it and refuses if it has changed
            formData.append('modelString', encodeURIComponent(JSON.stringify({ ...payout, referenceNumber: referenceNumber.trim() })));
            if (slipFile) {
                formData.append('file', slipFile);
            }
            const response: ResponseModel = await transferVideoPayout(admin, formData);
            if (response.status) {
                onDone(true, `Payout of ${formatAmount(payout.netAmount)} to ${payout.teacherName} is recorded.`);
            }
            else {
                onDone(false, response.message || "Oops! something went wrong.");
            }
        } catch (error) {
            toErrorPage(error);
        }
    }

    return (
        <PopUpDialog styles={styles} open={true} toggle={isSaving ? undefined : onClose}>
            <h2 style={styles.title}>{`Transfer to ${payout.teacherName}`}</h2>
            <div style={styles.mutedText}>{`${payout.purchaseCount} purchases up to ${getFormatedDate(payout.periodEndDate)}`}</div>
            <div style={styles.amountRow}>
                <AmountTile styles={styles.amountTile} value={formatAmount(payout.grossAmount)} label="Gross" />
                <AmountTile styles={styles.amountTile} value={formatAmount(payout.commissionAmount)} label={`Commission (${payout.commissionRate}%)`} />
                <AmountTile styles={styles.amountTile} value={formatAmount(payout.netAmount)} label="Net to transfer" highlight />
            </div>
            <PayoutBankAccount payout={payout} styles={styles} />
            <div style={styles.sectionTitle}>{"Purchases"}</div>
            <PurchaseList purchases={purchases} styles={styles} />
            <div style={styles.field}>
                <label style={styles.label}>{"Bank transfer reference"}</label>
                <input type="text" style={styles.textInput} value={referenceNumber} maxLength={100} onChange={(e) => setReferenceNumber(e.target.value)} />
            </div>
            <div style={styles.field}>
                <label style={styles.label}>{"Transfer slip (pdf or image)"}</label>
                <label style={styles.fileButton}>
                    {slipFile ? `Selected: ${slipFile.name}` : "Choose slip"}
                    <input type="file" accept="application/pdf,image/*" onChange={handleSlipChange} style={{ display: 'none' }} />
                </label>
                {slipFile && !isSaving && <span style={styles.removeFile} onClick={() => setSlipFile(undefined)}>{"Remove"}</span>}
            </div>
            <div style={styles.buttonWrapper}>
                <Button style={{...styles.cancelButton, ...(isSaving && styles.disabledButton)}} disabled={isSaving} onClick={onClose}>{"Cancel"}</Button>
                {/* Wait for the purchases so the admin has seen what the transfer covers */}
                <Button
                    style={{...styles.confirmButton, ...((isSaving || purchases === undefined) && styles.disabledButton)}}
                    disabled={isSaving || purchases === undefined}
                    onClick={confirmTransfer}
                >
                    {isSaving ? "Saving..." : "Mark as Transferred"}
                </Button>
            </div>
            <Alert />
        </PopUpDialog>
    );
}

interface PayoutDetailDialogProps {
    admin: LoggedAdmin;
    payout: VideoPayout;
    styles: any;
    onClose(): void;
}

// Read-only view of a completed payout: the saved amounts, bank details and transfer, and the purchases it covered
const PayoutDetailDialog: React.FC<PayoutDetailDialogProps> = ({ admin, payout, styles, onClose }) => {
    const [purchases, setPurchases] = useState<VideoPayoutPurchase[]>();
    const [toErrorPage] = useErrorPage();

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const purchaseList: VideoPayoutPurchase[] = await getVideoPayoutPurchases(admin, payout.videoLessonPayoutId);
                if (active) setPurchases(purchaseList);
            } catch (error) {
                if (active) toErrorPage(error);
            }
        })();
        return () => { active = false; };
    }, []);

    return (
        <PopUpDialog styles={styles} open={true} toggle={onClose}>
            <h2 style={styles.title}>{`Payout #${payout.videoLessonPayoutId} to ${payout.teacherName}`}</h2>
            <div style={styles.mutedText}>{`${payout.purchaseCount} purchases up to ${getFormatedDate(payout.periodEndDate)}`}</div>
            <div style={styles.amountRow}>
                <AmountTile styles={styles.amountTile} value={formatAmount(payout.grossAmount)} label="Gross" />
                <AmountTile styles={styles.amountTile} value={formatAmount(payout.commissionAmount)} label={`Commission (${payout.commissionRate}%)`} />
                <AmountTile styles={styles.amountTile} value={formatAmount(payout.netAmount)} label="Net transferred" highlight />
            </div>
            <PayoutBankAccount payout={payout} styles={styles} />
            <div style={styles.transferDetails}>
                <div><span style={styles.bankLabel}>{"Transferred on: "}</span>{getFormatedDate(payout.createdDate!)}</div>
                {payout.transferredByName && <div><span style={styles.bankLabel}>{"Transferred by: "}</span>{payout.transferredByName}</div>}
                <div><span style={styles.bankLabel}>{"Reference: "}</span>{payout.referenceNumber || "-"}</div>
                <div>
                    <span style={styles.bankLabel}>{"Slip: "}</span>
                    {payout.hasSlip ?
                    <a style={styles.link} href={getVideoPayoutSlipUrl(payout.videoLessonPayoutId, admin.adminId, admin.token)} target="_blank" rel="noreferrer">{"View slip"}</a> :
                    "No slip"}
                </div>
            </div>
            <div style={styles.sectionTitle}>{"Purchases"}</div>
            <PurchaseList purchases={purchases} styles={styles} />
            <div style={styles.buttonWrapper}>
                <Button style={styles.cancelButton} onClick={onClose}>{"Close"}</Button>
            </div>
        </PopUpDialog>
    );
}

// Pending payouts show the teacher's current account; completed payouts show the account saved at transfer time
const PayoutBankAccount: React.FC<{ payout: VideoPayout, styles: any }> = ({ payout, styles }) => (
    <div style={styles.bank}>
        <div><span style={styles.bankLabel}>{"Account holder: "}</span>{payout.accountHolderName}</div>
        <div><span style={styles.bankLabel}>{"Account number: "}</span>{payout.accountNumber}</div>
        <div><span style={styles.bankLabel}>{"Bank: "}</span>{payout.bankName}</div>
        <div><span style={styles.bankLabel}>{"Branch: "}</span>{payout.branchName}</div>
    </div>
)

// Purchases grouped by video subject, shared by the transfer and payout detail dialogs
const PurchaseList: React.FC<{ purchases?: VideoPayoutPurchase[], styles: any }> = ({ purchases, styles }) => {
    if (purchases === undefined) return <TableLoader columnCount={4} rowCount={3} />;

    return (
        <>
            {groupBySubject(purchases).map((group) =>
                <div key={group.videoSubjectId} style={styles.subjectGroup}>
                    <div style={styles.subjectHeader}>
                        <div>
                            <div style={styles.subjectHeader.subjects}>{group.subjects}</div>
                            {group.grades && <div style={styles.mutedText}>{group.grades}</div>}
                        </div>
                        <div style={styles.subjectHeader.total}>
                            {`${group.purchases.length} · ${formatAmount(group.purchases.reduce((total, purchase) => total + purchase.amount, 0))}`}
                        </div>
                    </div>
                    <table style={styles.purchaseTable}>
                        <thead>
                            <tr>
                                <th style={styles.purchaseTable.th}>{"Date"}</th>
                                <th style={styles.purchaseTable.th}>{"Lesson"}</th>
                                <th style={styles.purchaseTable.th}>{"Student"}</th>
                                <th style={styles.purchaseTable.th}>{"Payment"}</th>
                                <th style={{...styles.purchaseTable.th, ...styles.purchaseTable.amount}}>{"Amount"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.purchases.map((purchase) =>
                                <tr key={purchase.videoLessonPurchaseId}>
                                    <td style={styles.purchaseTable.td}>{getFormatedDate(purchase.purchasedDate)}</td>
                                    <td style={styles.purchaseTable.td}>{purchase.lessonName}</td>
                                    <td style={styles.purchaseTable.td}>{purchase.studentName}</td>
                                    <td style={styles.purchaseTable.td}>
                                        <div>{purchase.paymentSource}</div>
                                        {purchase.refferenceNumber && <div style={styles.mutedText}>{purchase.refferenceNumber}</div>}
                                    </td>
                                    <td style={{...styles.purchaseTable.td, ...styles.purchaseTable.amount}}>{formatAmount(purchase.amount)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}

const AmountTile: React.FC<{ styles: any, value: string, label: string, highlight?: boolean }> = ({ styles, value, label, highlight }) => (
    <div style={styles}>
        <div style={{...styles.value, ...(highlight && styles.net)}}>{value}</div>
        <div style={styles.label}>{label}</div>
    </div>
)
